import React, { useEffect, useMemo, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import client from '../../api/client';
import { Plus, Users, Sparkles, Trash2, Loader2, Printer, X, Utensils, ChevronDown, ChevronUp, Move, Wand2 } from 'lucide-react';
import InfoTip from '../../components/InfoTip';

export default function Seating() {
  const [tables, setTables] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showPrint, setShowPrint] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showAllUnassigned, setShowAllUnassigned] = useState(false);
  const [lateOnly, setLateOnly] = useState(false);
  const [aiDesc, setAiDesc] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiToast, setAiToast] = useState('');

  // Pointer sensor with an 8px activation delta so clicks on table
  // circles (to select) don't accidentally start a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  useEffect(() => {
    (async () => {
      const [{ data: s }, { data: g }] = await Promise.all([
        client.get('/seating'),
        client.get('/guests'),
      ]);
      setTables(s.tables);
      setGuests(g);
      setSelected(s.tables[0]?.id || null);
      setLoading(false);
    })();
  }, []);

  const addTable = async () => {
    const nextNum = (tables.length + 1);
    const { data } = await client.post('/seating/tables', { label: `Table ${nextNum}`, seats: 8, guests: [] });
    setTables((prev) => [...prev, data]);
    setSelected(data.id);
  };

  const updateTable = async (id, patch) => {
    setTables((prev) => prev.map(t => t.id === id ? { ...t, ...patch } : t));
    try { await client.put(`/seating/tables/${id}`, patch); } catch (e) { /* ignore */ }
  };

  const removeTable = async (id) => {
    setTables((prev) => prev.filter(t => t.id !== id));
    if (selected === id) setSelected(tables[0]?.id || null);
    try { await client.delete(`/seating/tables/${id}`); } catch (e) { /* ignore */ }
  };

  const addGuestToTable = (id, name) => {
    const t = tables.find(tt => tt.id === id);
    if (!t || !name.trim()) return;
    updateTable(id, { guests: [...t.guests, name.trim()] });
  };

  const removeGuestFromTable = (id, idx) => {
    const t = tables.find(tt => tt.id === id);
    if (!t) return;
    updateTable(id, { guests: t.guests.filter((_, i) => i !== idx) });
  };

  // Update a guest's `table` field (used by drill-downs from this tab).
  const setGuestTable = async (guestId, tableNum) => {
    setGuests((prev) => prev.map(g => g.id === guestId ? { ...g, table: String(tableNum || '') } : g));
    try { await client.put(`/guests/${guestId}`, { table: String(tableNum || '') }); } catch (e) { /* ignore */ }
  };

  // Extract the numeric portion from a table label like "Table 3" or "3" — used
  // to auto-group guests from the guest list by their `table` field.
  const tableNumOf = (label) => {
    const m = String(label || '').match(/(\d+)/);
    return m ? m[1] : '';
  };

  // Guests-from-list, grouped by table number.
  const guestsByTableNum = useMemo(() => {
    const map = {};
    (guests || []).forEach((g) => {
      const t = String(g.table || '').trim();
      if (!t) return;
      if (!map[t]) map[t] = [];
      map[t].push(g);
      if (g.plusOne && g.plusOneName) {
        map[t].push({ ...g, id: `${g.id}-plusone`, name: g.plusOneName, isPlusOne: true });
      }
    });
    return map;
  }, [guests]);

  const unassigned = useMemo(() =>
    (guests || []).filter(g => !String(g.table || '').trim() && g.rsvp !== 'No'),
  [guests]);

  if (loading) return <div className="flex items-center gap-2 text-[#6A6A6A]"><Loader2 className="w-4 h-4 animate-spin" /> Loading seating…</div>;

  const active = tables.find(t => t.id === selected);
  const activeNum = active ? tableNumOf(active.label) : '';
  const activeGuestsFromList = activeNum ? (guestsByTableNum[activeNum] || []) : [];

  // Drop handler — id shapes:
  //   guest chip active.id = "guest:<guestId>"
  //   drop zone over.id   = "table:<tableId>"  OR  "unassigned"
  //   table drag active.id = "table:<tableId>"  (positional move on canvas)
  const handleDragEnd = (evt) => {
    const { active: a, over, delta } = evt;
    if (!a || !over) return;
    const aId = String(a.id);
    const oId = String(over.id);
    // Reposition a table on the canvas
    if (aId.startsWith('table:') && oId === 'canvas') {
      const tid = aId.slice(6);
      const rect = a.rect?.current?.initial || a.rect?.current?.translated;
      const canvasRect = evt.collisions?.[0]?.data?.droppableContainer?.rect?.current;
      const w = (canvasRect?.width) || 600;
      const h = (canvasRect?.height) || 480;
      const t = tables.find(tt => tt.id === tid);
      if (!t) return;
      const nextX = Math.max(5, Math.min(90, (Number(t.x) || 20) + ((delta?.x || 0) / w) * 100));
      const nextY = Math.max(5, Math.min(75, (Number(t.y) || 20) + ((delta?.y || 0) / h) * 100));
      updateTable(tid, { x: nextX, y: nextY });
      return;
    }
    // Assign a guest to a table (or unassign)
    if (aId.startsWith('guest:')) {
      const gid = aId.slice(6);
      if (oId === 'unassigned') {
        setGuestTable(gid, '');
        return;
      }
      if (oId.startsWith('table:')) {
        const dropTid = oId.slice(6);
        const target = tables.find(tt => tt.id === dropTid);
        if (!target) return;
        const num = tableNumOf(target.label);
        setGuestTable(gid, num || '');
      }
    }
  };

  const runAiLayout = async () => {
    if (aiBusy) return;
    if (!aiDesc.trim()) { setAiToast('Describe your reception layout first.'); setTimeout(() => setAiToast(''), 3000); return; }
    setAiBusy(true);
    try {
      const { data } = await client.post('/seating/ai-layout', { description: aiDesc });
      setTables(data.tables || []);
      setAiToast(data.ai_used ? 'Layout arranged by your AI wedding friend.' : 'Layout applied using a fallback grid.');
      setTimeout(() => setAiToast(''), 3500);
    } catch { setAiToast('AI layout failed. Try again.'); setTimeout(() => setAiToast(''), 3000); }
    finally { setAiBusy(false); }
  };

  const isLate = (g) => !!(g.reply_by_override || g.late_invite_at || g.reopened_at);
  const lateCount = unassigned.filter(isLate).length;
  const pool = lateOnly ? unassigned.filter(isLate) : unassigned;
  const unassignedVisible = showAllUnassigned ? pool : pool.slice(0, 20);

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
    <div className="animate-float-up">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-4xl text-[#2A2A2A] mb-1 flex items-center gap-3"><AppIcon feature="seating" size="lg" reveal testId="section-icon-seating" />Seating plan</h1>
          <p className="text-[#6A6A6A]">Click a table to manage its guests, or assign a table on any guest row in the Guests tab.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="seating-print-cards-btn"
            onClick={() => setShowPrint(true)}
            className="text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 bg-white border border-[#B8896C] text-[#7B2C3B] hover:bg-[#F4E4DA]"
          >
            <Printer className="w-4 h-4" /> Print table cards
          </button>
          <button
            data-testid="seating-print-menu-btn"
            onClick={() => setShowMenu(true)}
            className="text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 bg-white border border-[#B8896C] text-[#7B2C3B] hover:bg-[#F4E4DA]"
          >
            <Utensils className="w-4 h-4" /> Print menu cards
          </button>
          <button onClick={addTable} data-testid="seating-add-table" className="btn-primary text-sm px-4 py-2 rounded-full inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add table</button>
        </div>
      </div>

      {unassigned.length > 0 && (
        <UnassignedDropZone count={unassigned.length}>
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <p className="text-sm font-medium text-[#7B4A1A] inline-flex items-center gap-1">
                {unassigned.length} guest{unassigned.length === 1 ? '' : 's'} without a table
                <InfoTip testId="info-unassigned" title="Drag to seat">
                  <p>Drag any of these guest chips onto a table circle below to assign them. Drag them back here to unassign. You can also click on a table to open it and drag from the "From guest list" panel.</p>
                </InfoTip>
                {lateCount > 0 && (
                  <button data-testid="unassigned-late-filter" onClick={() => setLateOnly((v) => !v)}
                          className={`ml-2 text-[11px] px-2.5 py-0.5 rounded-full border transition-colors ${lateOnly ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'border-[#E8C9A0] text-[#7B4A1A] hover:border-[#7B2C3B]'}`}>
                    Late invites ({lateCount})
                  </button>
                )}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {unassignedVisible.map((g) => (
                  <GuestChip key={g.id} id={g.id} name={g.name} relationship={g.relationship} late={isLate(g)} compact />
                ))}
                {pool.length > 20 && (
                  <button
                    data-testid="unassigned-toggle-all"
                    onClick={() => setShowAllUnassigned((v) => !v)}
                    className="text-[11px] text-[#7B4A1A] hover:underline inline-flex items-center gap-1 self-center"
                  >
                    {showAllUnassigned
                      ? <>Show first 20 only <ChevronUp className="w-3 h-3" /></>
                      : <>Show all {unassigned.length} <ChevronDown className="w-3 h-3" /></>
                    }
                  </button>
                )}
              </div>
            </div>
          </div>
        </UnassignedDropZone>
      )}

      {/* AI reception-layout parser */}
      <div className="soft-card rounded-2xl p-4 mb-6 bg-white border border-[#EFE3D8]">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <p className="section-eyebrow mb-2 inline-flex items-center gap-1"><Wand2 className="w-3 h-3" /> AI reception layout</p>
            <input
              data-testid="ai-layout-input"
              value={aiDesc}
              onChange={(e) => setAiDesc(e.target.value)}
              placeholder='e.g. "8 round tables in two rows around a dance floor, sweetheart at the head"'
              className="w-full px-3 py-2 rounded-full border border-[#EFE3D8] text-sm focus:outline-none focus:border-[#7B2C3B]"
            />
          </div>
          <button
            data-testid="ai-layout-run"
            onClick={runAiLayout}
            disabled={aiBusy}
            className="btn-primary text-xs px-4 py-2 rounded-full inline-flex items-center gap-1.5 disabled:opacity-60 flex-shrink-0 self-end"
          >
            {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {aiBusy ? 'Arranging…' : 'Arrange tables'}
          </button>
        </div>
        {aiToast && <p className="mt-2 text-xs text-[#7B2C3B]" data-testid="ai-layout-toast">{aiToast}</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 soft-card rounded-2xl p-6 bg-[#F6EAE0] relative overflow-hidden min-h-[520px]">
          <p className="section-eyebrow mb-3">Reception layout <span className="text-[10px] text-[#9A8A7E] normal-case tracking-normal ml-1">· drag circles to reposition</span></p>
          {tables.some(t => Number.isFinite(Number(t.x)) && Number.isFinite(Number(t.y))) ? (
            <div className="relative" style={{ height: 440 }} data-testid="reception-canvas-positioned">
              {tables.map((t) => {
                const num = tableNumOf(t.label);
                const fromList = num ? (guestsByTableNum[num] || []).length : 0;
                const total = fromList + (t.guests || []).length;
                return (
                  <ReceptionTableCircle
                    key={t.id}
                    table={t}
                    isSelected={t.id === selected}
                    guestsCount={total}
                    onSelect={setSelected}
                  />
                );
              })}
            </div>
          ) : (
            <div className="flex flex-wrap gap-6 items-start justify-center pt-4">
              {tables.map((t) => {
                const num = tableNumOf(t.label);
                const fromList = num ? (guestsByTableNum[num] || []).length : 0;
                const total = fromList + (t.guests || []).length;
                return (
                  <ReceptionTableCircle
                    key={t.id}
                    table={t}
                    isSelected={t.id === selected}
                    guestsCount={total}
                    onSelect={setSelected}
                  />
                );
              })}
            </div>
          )}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-[#9A8A7E] font-serif italic pointer-events-none">— dance floor —</div>
        </div>

        {active ? (
          <div className="soft-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <input value={active.label} onChange={(e) => updateTable(active.id, { label: e.target.value })} className="font-serif text-2xl text-[#2A2A2A] bg-transparent focus:outline-none border-b border-transparent focus:border-[#B8896C] w-full" />
              <span className="text-xs px-2.5 py-1 rounded-full tag-chip flex items-center gap-1 flex-shrink-0"><Users className="w-3 h-3" /> {activeGuestsFromList.length + (active.guests || []).length}/{active.seats}</span>
            </div>
            <div className="flex items-center gap-2 mb-3 text-xs text-[#6A6A6A]">
              Seats: <input type="number" value={active.seats} onChange={(e) => updateTable(active.id, { seats: Number(e.target.value) || 1 })} className="w-14 bg-transparent focus:outline-none border-b border-transparent focus:border-[#B8896C]" />
            </div>

            {activeGuestsFromList.length > 0 && activeNum && (
              <div className="mb-3">
                <p className="text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1.5">From guest list · Table {activeNum} <span className="normal-case tracking-normal text-[10px]">— drag to move</span></p>
                <div className="flex flex-wrap gap-1.5 max-h-56 overflow-y-auto">
                  {activeGuestsFromList.map((g) => (
                    g.isPlusOne ? (
                      <div key={g.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FBF7F2] border border-[#EFE3D8] text-xs text-[#9A8A7E]" data-testid={`seating-plusone-${g.id}`}>
                        {g.name} <span className="text-[10px]">(+1)</span>
                      </div>
                    ) : (
                      <GuestChip
                        key={g.id}
                        id={g.id}
                        name={g.name}
                        relationship={g.relationship}
                        onUnassign={() => setGuestTable(g.id, '')}
                        compact
                      />
                    )
                  ))}
                </div>
              </div>
            )}

            {(active.guests || []).length > 0 && (
              <p className="text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1.5 mt-2">Manual additions</p>
            )}
            <ul className="space-y-2 mb-3 max-h-72 overflow-y-auto">
              {(active.guests || []).map((g, i) => (
                <li key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl border border-[#EFE3D8] text-sm text-[#2A2A2A] hover:border-[#B8896C]">
                  <span className="w-6 h-6 rounded-full bg-[#F4D6CE] text-[#7B2C3B] text-xs flex items-center justify-center font-medium">{activeGuestsFromList.length + i + 1}</span>
                  <span className="flex-1">{g}</span>
                  <button onClick={() => removeGuestFromTable(active.id, i)} className="text-[#B8896C] hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
            <AddGuestInput onAdd={(name) => addGuestToTable(active.id, name)} />
            <button onClick={() => removeTable(active.id)} className="mt-4 text-xs text-[#B8896C] hover:text-red-600 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete table</button>
            <div className="mt-4 p-3 rounded-xl bg-[#F6EAE0] flex items-start gap-2 text-xs text-[#7B2C3B]">
              <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              Tip: name tables "Table 1", "Table 2" — that number is what lets us auto-group guests from the guest list.
            </div>
          </div>
        ) : (
          <div className="soft-card rounded-2xl p-6 text-sm text-[#6A6A6A]">No tables yet. Click “Add table” to start.</div>
        )}
      </div>

      {showPrint && (
        <PrintTableCards
          tables={tables}
          guestsByTableNum={guestsByTableNum}
          tableNumOf={tableNumOf}
          onClose={() => setShowPrint(false)}
        />
      )}
      {showMenu && (
        <PrintMenuCards
          tables={tables}
          guests={guests}
          guestsByTableNum={guestsByTableNum}
          tableNumOf={tableNumOf}
          onClose={() => setShowMenu(false)}
        />
      )}
    </div>
    </DndContext>
  );
}

// -----------------------------------------------------------------------------
// Draggable guest chip — used in the "Unassigned" strip and the "From guest
// list" panel on the active table. `sourceLabel` renders as small subtitle.
// -----------------------------------------------------------------------------
function GuestChip({ id, name, isPlusOne, relationship, onUnassign, compact, late }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `guest:${id}`,
  });
  const style = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    opacity: isDragging ? 0.6 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    touchAction: 'none',
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      data-testid={`guest-chip-${id}`}
      className={`inline-flex items-center gap-1.5 rounded-full bg-white border ${compact ? 'px-2.5 py-1 border-[#F5D9B7] text-xs' : 'px-3 py-1.5 border-[#EFE3D8] text-sm'} select-none`}
    >
      <Move className="w-3 h-3 text-[#B8896C] flex-shrink-0" />
      <span className="text-[#2A2A2A]">{name}</span>
      {isPlusOne && <span className="text-[10px] text-[#9A8A7E]">(+1)</span>}
      {relationship && <span className="text-[10px] text-[#9A8A7E] italic">· {relationship}</span>}
      {late && <span data-testid={`late-chip-badge-${id}`} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#7B2C3B] text-white">Late</span>}
      {onUnassign && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onUnassign(); }}
          onPointerDown={(e) => e.stopPropagation()}
          className="text-[#B8896C] hover:text-red-600 ml-0.5"
          title="Unassign from this table"
          data-testid={`guest-chip-unassign-${id}`}
        ><X className="w-3 h-3" /></button>
      )}
    </div>
  );
}

// Droppable table circle on the reception-layout canvas. Absolute-
// positioned via {left, top} percentages. Click to select. Long-press-
// then-drag to reposition (dnd-kit's 8px activation constraint keeps
// clicks clean).
function ReceptionTableCircle({ table, isSelected, guestsCount, onSelect }) {
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `table:${table.id}` });
  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
    id: `table:${table.id}`,
  });
  const size = String(table.label || '').toLowerCase().includes('sweetheart') ? 100 : 120;
  const x = Number.isFinite(Number(table.x)) ? Number(table.x) : null;
  const y = Number.isFinite(Number(table.y)) ? Number(table.y) : null;
  const positioned = x !== null && y !== null;
  const style = positioned ? {
    position: 'absolute',
    left: `${x}%`,
    top: `${y}%`,
    transform: `translate(-50%, -50%)${transform ? ` translate(${transform.x}px, ${transform.y}px)` : ''}`,
    width: size,
    height: size,
    touchAction: 'none',
  } : { width: size, height: size, touchAction: 'none' };
  const setBoth = (node) => { setDropRef(node); setDragRef(node); };
  return (
    <button
      ref={setBoth}
      style={style}
      onClick={() => onSelect(table.id)}
      {...listeners}
      {...attributes}
      data-testid={`reception-table-${table.id}`}
      className={`rounded-full border-2 transition-all flex flex-col items-center justify-center text-center px-3 select-none ${isSelected ? 'border-[#7B2C3B] bg-[#FBF7F2] shadow-lg scale-105' : 'border-[#B8896C] bg-[#FBF7F2]/70 hover:scale-105'} ${isOver ? 'ring-4 ring-[#7B2C3B]/40' : ''} ${isDragging ? 'opacity-70' : ''}`}
    >
      <span className="font-serif text-sm text-[#2A2A2A] leading-tight">{table.label}</span>
      <span className="text-[10px] text-[#6A6A6A] mt-1">{guestsCount} / {table.seats}</span>
    </button>
  );
}

function UnassignedDropZone({ children, count }) {
  const { setNodeRef, isOver } = useDroppable({ id: 'unassigned' });
  return (
    <div
      ref={setNodeRef}
      data-testid="unassigned-drop-zone"
      className={`soft-card rounded-2xl p-4 mb-6 bg-[#FFF7EC] border ${isOver ? 'border-[#7B2C3B] ring-2 ring-[#7B2C3B]/30' : 'border-[#F5D9B7]'} transition-all`}
    >
      {children}
    </div>
  );
}

function AddGuestInput({ onAdd }) {
  const [val, setVal] = useState('');
  return (
    <form onSubmit={(e) => { e.preventDefault(); onAdd(val); setVal(''); }} className="flex gap-2">
      <input value={val} onChange={(e) => setVal(e.target.value)} placeholder="Add guest name" className="flex-1 px-3 py-2 rounded-full border border-[#EFE3D8] text-sm focus:outline-none focus:border-[#7B2C3B]" />
      <button type="submit" className="btn-primary text-xs px-3 py-2 rounded-full">Add</button>
    </form>
  );
}

// -----------------------------------------------------------------------------
// Printable centerpiece cards — one table per page, elegant serif style.
// A modal with an in-browser preview + Print button that opens the browser
// print dialog. Print CSS sizes each card to a full page.
// -----------------------------------------------------------------------------
function PrintTableCards({ tables, guestsByTableNum, tableNumOf, onClose }) {
  const [selectedIds, setSelectedIds] = useState(() => new Set(tables.map(t => t.id)));

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(tables.map(t => t.id)));
  const selectNone = () => setSelectedIds(new Set());

  // Names to print for a table = union of guest-list guests and manual guests
  const namesFor = (t) => {
    const num = tableNumOf(t.label);
    const fromList = num ? (guestsByTableNum[num] || []).map(g => g.name) : [];
    const manual = t.guests || [];
    // Dedupe (case-insensitive) so a manually-added name doesn't repeat.
    const seen = new Set();
    const out = [];
    [...fromList, ...manual].forEach((n) => {
      const k = (n || '').trim().toLowerCase();
      if (!k || seen.has(k)) return;
      seen.add(k);
      out.push(n.trim());
    });
    return out;
  };

  const cards = tables
    .filter(t => selectedIds.has(t.id))
    .map(t => ({ id: t.id, label: t.label, number: tableNumOf(t.label), names: namesFor(t) }));

  const doPrint = () => {
    window.print();
  };

  // ESC key closes; also lock body scroll while modal is open so users
  // can't get confused about what's scrolling underneath.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #print-table-cards, #print-table-cards * { visibility: visible !important; }
          #print-table-cards { position: absolute; left: 0; top: 0; width: 100%; }
          .print-hidden { visibility: hidden !important; }
          .table-card-page {
            width: 100%;
            min-height: 100vh;
            page-break-after: always;
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
          }
          .table-card-page:last-child { page-break-after: auto; }
          @page { size: letter portrait; margin: 0.5in; }
        }
      `}</style>

      <div
        className="print-hidden fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="table-cards-backdrop"
      >
        <div
          className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between gap-3 flex-shrink-0 bg-white">
            <div className="min-w-0">
              <h2 className="font-serif text-xl text-[#2A2A2A]">Print table cards</h2>
              <p className="text-xs text-[#6A6A6A]">One card per page — table number bold up top, guest names below. Perfect as a centerpiece.</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button data-testid="print-cards-select-all" onClick={selectAll} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C]">Select all</button>
              <button data-testid="print-cards-select-none" onClick={selectNone} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C]">Clear</button>
              <button data-testid="print-cards-print" onClick={doPrint} className="btn-primary text-xs px-4 py-1.5 rounded-full inline-flex items-center gap-1.5">
                <Printer className="w-3.5 h-3.5" /> Print {cards.length ? `(${cards.length})` : ''}
              </button>
              <button data-testid="print-cards-close" onClick={onClose} title="Close (Esc)" className="text-[#9A8A7E] hover:text-[#7B2C3B] p-1"><X className="w-5 h-5" /></button>
            </div>
          </div>

          <div className="p-4 border-b border-[#EFE3D8] bg-[#FBF7F2]">
            <p className="text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-2">Which tables?</p>
            <div className="flex flex-wrap gap-2">
              {tables.map((t) => {
                const on = selectedIds.has(t.id);
                return (
                  <button
                    key={t.id}
                    data-testid={`print-cards-toggle-${t.id}`}
                    onClick={() => toggle(t.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? 'bg-[#7B2C3B] text-[#FBF7F2] border-[#7B2C3B]' : 'bg-white text-[#3A3A3A] border-[#EFE3D8] hover:border-[#B8896C]'}`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-[#F6EAE0]/40">
            <div id="print-table-cards" data-testid="print-table-cards-preview">
              {cards.length === 0 && (
                <p className="text-center text-sm text-[#6A6A6A] py-16">Select at least one table to preview a card.</p>
              )}
              {cards.map((c) => (
                <TableCard key={c.id} label={c.label} number={c.number} names={c.names} />
              ))}
            </div>
          </div>

          {/* Sticky bottom bar — always in reach even if the huge 10.5in
              preview pushes the top header out of view. */}
          <div className="p-3 border-t border-[#EFE3D8] flex items-center justify-between gap-2 flex-shrink-0 bg-white">
            <button data-testid="print-cards-back" onClick={onClose} className="text-xs px-4 py-2 rounded-full border border-[#EFE3D8] hover:border-[#B8896C] text-[#3A3A3A]">
              ← Back to seating
            </button>
            <button data-testid="print-cards-print-bottom" onClick={doPrint} className="btn-primary text-xs px-4 py-2 rounded-full inline-flex items-center gap-1.5">
              <Printer className="w-3.5 h-3.5" /> Print {cards.length ? `(${cards.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function TableCard({ label, number, names }) {
  return (
    <div className="table-card-page mx-auto my-6 bg-[#FBF7F2] border border-[#EFE3D8] shadow-sm rounded-lg overflow-hidden flex flex-col items-center justify-center text-center relative" style={{ width: '8in', minHeight: '10.5in', padding: '0.75in 0.75in' }}>
      {/* Ornament */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-3 text-[#B8896C]">
        <span className="block w-14 h-px bg-[#B8896C]" />
        <span className="text-xs tracking-[0.4em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Table</span>
        <span className="block w-14 h-px bg-[#B8896C]" />
      </div>

      {/* Big number */}
      <div className="mt-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        <div
          className="font-bold leading-none"
          style={{ color: '#8A6A32', fontSize: number ? '9rem' : '5rem', letterSpacing: '-0.02em' }}
        >
          {number || label}
        </div>
      </div>

      {/* Separator */}
      <div className="mt-8 flex items-center gap-3 text-[#B8896C]">
        <span className="block w-24 h-px bg-[#B8896C]" />
        <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="italic text-lg">seated together</span>
        <span className="block w-24 h-px bg-[#B8896C]" />
      </div>

      {/* Names */}
      <ul className="mt-8 space-y-2.5 w-full max-w-[5in]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        {names.length === 0 && (
          <li className="text-center italic text-[#9A8A7E]" style={{ fontSize: '1.2rem' }}>No guests assigned yet</li>
        )}
        {names.map((n, i) => (
          <li key={i} className="text-center text-[#2A2A2A]" style={{ fontSize: '1.35rem', lineHeight: '1.35' }}>
            {n}
          </li>
        ))}
      </ul>

      {/* Bottom ornament */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#B8896C]">
        <span className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>♥</span>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Printable menu cards — two modes toggle in the modal:
//   • by-guest → one card per guest (Name + meal + dietary) — place setting
//   • by-table → one card per table listing everyone + their meals — kitchen ref
// Both use the same elegant serif print CSS as the table cards.
// -----------------------------------------------------------------------------
function PrintMenuCards({ tables, guests, guestsByTableNum, tableNumOf, onClose }) {
  const [mode, setMode] = useState('by-guest');
  const [selectedTableIds, setSelectedTableIds] = useState(() => new Set(tables.map(t => t.id)));

  const toggle = (id) => {
    setSelectedTableIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedTableIds(new Set(tables.map(t => t.id)));
  const selectNone = () => setSelectedTableIds(new Set());

  // Build the list of (guest, tableLabel, tableNum) rows for cards.
  const rows = useMemo(() => {
    const includedNums = new Set(
      tables.filter(t => selectedTableIds.has(t.id)).map(t => tableNumOf(t.label))
    );
    return (guests || [])
      .filter(g => {
        const t = String(g.table || '').trim();
        return t && includedNums.has(t);
      })
      .flatMap(g => {
        const tableLabel = tables.find(t => tableNumOf(t.label) === String(g.table))?.label || `Table ${g.table}`;
        const own = { name: g.name, meal: g.meal && g.meal !== '—' ? g.meal : '', dietary: g.dietary || '', tableLabel, tableNum: g.table };
        if (g.plusOne && g.plusOneName) {
          const plus = { name: g.plusOneName, meal: '', dietary: '', tableLabel, tableNum: g.table };
          return [own, plus];
        }
        return [own];
      });
  }, [guests, tables, selectedTableIds, tableNumOf]);

  // For by-table mode: group rows by tableNum
  const groupedByTable = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (!map.has(r.tableNum)) map.set(r.tableNum, { label: r.tableLabel, num: r.tableNum, guests: [] });
      map.get(r.tableNum).guests.push(r);
    });
    return Array.from(map.values()).sort((a, b) => Number(a.num) - Number(b.num));
  }, [rows]);

  const doPrint = () => window.print();

  // Same ESC + body-scroll-lock UX as the table-cards modal.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #print-menu-cards, #print-menu-cards * { visibility: visible !important; }
          #print-menu-cards { position: absolute; left: 0; top: 0; width: 100%; }
          .print-hidden { visibility: hidden !important; }
          .menu-card-page {
            width: 100%;
            min-height: 100vh;
            page-break-after: always;
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
          }
          .menu-card-page:last-child { page-break-after: auto; }
          @page { size: letter portrait; margin: 0.5in; }
        }
      `}</style>

      <div
        className="print-hidden fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        data-testid="menu-cards-modal"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between gap-3 flex-shrink-0 bg-white">
            <div className="min-w-0">
              <h2 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                <Utensils className="w-5 h-5 text-[#7B2C3B]" /> Print menu cards
              </h2>
              <p className="text-xs text-[#6A6A6A]">Elegant serif cards for each place setting or a kitchen-side sheet per table.</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button data-testid="menu-cards-print" onClick={doPrint} className="btn-primary text-xs px-4 py-1.5 rounded-full inline-flex items-center gap-1.5">
                <Printer className="w-3.5 h-3.5" /> Print {rows.length ? `(${mode === 'by-guest' ? rows.length : groupedByTable.length})` : ''}
              </button>
              <button data-testid="menu-cards-close" onClick={onClose} title="Close (Esc)" className="text-[#9A8A7E] hover:text-[#7B2C3B] p-1"><X className="w-5 h-5" /></button>
            </div>
          </div>

          <div className="p-4 border-b border-[#EFE3D8] bg-[#FBF7F2] space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-2">Layout</p>
              <div className="inline-flex rounded-full border border-[#EFE3D8] p-0.5 bg-white">
                <button
                  data-testid="menu-mode-by-guest"
                  onClick={() => setMode('by-guest')}
                  className={`text-xs px-3 py-1.5 rounded-full ${mode === 'by-guest' ? 'bg-[#7B2C3B] text-[#FBF7F2]' : 'text-[#3A3A3A]'}`}
                >
                  One card per guest
                </button>
                <button
                  data-testid="menu-mode-by-table"
                  onClick={() => setMode('by-table')}
                  className={`text-xs px-3 py-1.5 rounded-full ${mode === 'by-table' ? 'bg-[#7B2C3B] text-[#FBF7F2]' : 'text-[#3A3A3A]'}`}
                >
                  One sheet per table (kitchen)
                </button>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wider text-[#9A8A7E]">Which tables?</p>
                <div className="flex items-center gap-1.5">
                  <button data-testid="menu-cards-select-all" onClick={selectAll} className="text-[10px] px-2 py-0.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C]">All</button>
                  <button data-testid="menu-cards-select-none" onClick={selectNone} className="text-[10px] px-2 py-0.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C]">None</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {tables.map((t) => {
                  const on = selectedTableIds.has(t.id);
                  return (
                    <button
                      key={t.id}
                      data-testid={`menu-cards-toggle-${t.id}`}
                      onClick={() => toggle(t.id)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? 'bg-[#7B2C3B] text-[#FBF7F2] border-[#7B2C3B]' : 'bg-white text-[#3A3A3A] border-[#EFE3D8] hover:border-[#B8896C]'}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-[#F6EAE0]/40">
            <div id="print-menu-cards" data-testid="menu-cards-preview">
              {rows.length === 0 && (
                <p className="text-center text-sm text-[#6A6A6A] py-16">No guests with a table assignment yet. Set table numbers on the guest list first.</p>
              )}
              {mode === 'by-guest' && rows.map((r, i) => (
                <MenuCardGuest key={`${r.name}-${i}`} {...r} />
              ))}
              {mode === 'by-table' && groupedByTable.map((g) => (
                <MenuCardTable key={g.num} label={g.label} number={g.num} guests={g.guests} />
              ))}
            </div>
          </div>

          <div className="p-3 border-t border-[#EFE3D8] flex items-center justify-between gap-2 flex-shrink-0 bg-white">
            <button data-testid="menu-cards-back" onClick={onClose} className="text-xs px-4 py-2 rounded-full border border-[#EFE3D8] hover:border-[#B8896C] text-[#3A3A3A]">
              ← Back to seating
            </button>
            <button data-testid="menu-cards-print-bottom" onClick={doPrint} className="btn-primary text-xs px-4 py-2 rounded-full inline-flex items-center gap-1.5">
              <Printer className="w-3.5 h-3.5" /> Print {rows.length ? `(${mode === 'by-guest' ? rows.length : groupedByTable.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function MenuCardGuest({ name, meal, dietary, tableLabel }) {
  return (
    <div className="menu-card-page mx-auto my-6 bg-[#FBF7F2] border border-[#EFE3D8] shadow-sm rounded-lg overflow-hidden flex flex-col items-center justify-center text-center relative" style={{ width: '5in', minHeight: '7in', padding: '0.6in 0.5in' }}>
      <div className="flex items-center gap-3 text-[#B8896C]">
        <span className="block w-10 h-px bg-[#B8896C]" />
        <span className="text-[10px] tracking-[0.4em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{tableLabel}</span>
        <span className="block w-10 h-px bg-[#B8896C]" />
      </div>

      <div className="mt-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        <div className="font-normal leading-tight text-[#2A2A2A]" style={{ fontSize: '2.6rem', letterSpacing: '-0.01em' }}>
          {name}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 text-[#B8896C]">
        <span className="block w-16 h-px bg-[#B8896C]" />
        <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="italic text-base">menu</span>
        <span className="block w-16 h-px bg-[#B8896C]" />
      </div>

      <div className="mt-6 space-y-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        {meal ? (
          <p className="text-[#2A2A2A]" style={{ fontSize: '1.4rem' }}>{meal}</p>
        ) : (
          <p className="italic text-[#9A8A7E]" style={{ fontSize: '1.15rem' }}>Chef&apos;s selection</p>
        )}
        {dietary && (
          <p className="italic text-[#7B2C3B]" style={{ fontSize: '0.95rem' }}>{dietary}</p>
        )}
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#B8896C]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        <span className="text-xl">♥</span>
      </div>
    </div>
  );
}

function MenuCardTable({ label, number, guests }) {
  return (
    <div className="menu-card-page mx-auto my-6 bg-[#FBF7F2] border border-[#EFE3D8] shadow-sm rounded-lg overflow-hidden relative" style={{ width: '8in', minHeight: '10.5in', padding: '0.75in 0.9in' }}>
      <div className="flex items-center gap-3 text-[#B8896C] justify-center">
        <span className="block w-12 h-px bg-[#B8896C]" />
        <span className="text-xs tracking-[0.4em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Table</span>
        <span className="block w-12 h-px bg-[#B8896C]" />
      </div>
      <div className="mt-3 text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        <div className="font-bold leading-none" style={{ color: '#8A6A32', fontSize: '5rem' }}>{number || label}</div>
      </div>
      <div className="mt-6 flex items-center gap-3 text-[#B8896C] justify-center">
        <span className="block w-20 h-px bg-[#B8896C]" />
        <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="italic text-lg">menu · kitchen sheet</span>
        <span className="block w-20 h-px bg-[#B8896C]" />
      </div>

      <table className="mt-6 w-full" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        <thead>
          <tr className="border-b border-[#B8896C]">
            <th className="text-left px-2 py-2 text-[#7B2C3B] text-xs uppercase tracking-wider font-medium">Guest</th>
            <th className="text-left px-2 py-2 text-[#7B2C3B] text-xs uppercase tracking-wider font-medium">Meal</th>
            <th className="text-left px-2 py-2 text-[#7B2C3B] text-xs uppercase tracking-wider font-medium">Dietary</th>
          </tr>
        </thead>
        <tbody>
          {guests.map((g, i) => (
            <tr key={i} className="border-b border-[#EFE3D8]">
              <td className="px-2 py-2.5 text-[#2A2A2A]" style={{ fontSize: '1.15rem' }}>{g.name}</td>
              <td className="px-2 py-2.5 text-[#2A2A2A]" style={{ fontSize: '1.05rem' }}>{g.meal || '—'}</td>
              <td className="px-2 py-2.5 italic text-[#7B2C3B]" style={{ fontSize: '0.95rem' }}>{g.dietary || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#B8896C]">
        <span className="text-xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>♥</span>
      </div>
    </div>
  );
}

